/**
 * Aim at the solid thing under the cursor (2026-09-23, the aim audit). The cursor is read on the ground (y = 0),
 * but everything standing up is drawn above the ground it stands on - at the game's 45 deg a point 3 m up is drawn
 * 3 m north of where it stands - so a cursor on a watchtower's cabin, a truck's cab or a lamp aimed several metres
 * beyond it, and the LAW, the round or the grenade went there. The audit (tests/unit/aimAudit.test.ts) found 27
 * target and weapon pairs that missed from some bearing, with the cursor plainly on the target.
 *
 * This follows the cursor's line of sight down into the world, as the camera sees it, and hands back the ground
 * point under the first solid it meets. Pure: the camera comes in as `factor` (1 / tan(pitch)).
 */

/** A solid as the cursor can meet it: its footprint on the ground and how tall it is drawn above `y`. */
export interface AimVolume {
  shape: 'circle' | 'box';
  x: number;
  z: number;
  /** Circle radius; box half-extents. */
  r: number;
  hx: number;
  hz: number;
  /** Height of the ground it stands on, metres. */
  y: number;
  /** How tall it is drawn, metres. */
  height: number;
}

/**
 * Along the cursor's line of sight a point `Y` metres up stands at (cursor.x, cursor.z + Y * factor). The highest
 * such point inside a volume is where the camera first sees it; of several volumes the one met highest is in
 * front. Returns that point's ground position, else null.
 */
export function aimVolume(
  cursor: { readonly x: number; readonly z: number },
  volumes: readonly AimVolume[],
  factor: number,
): { x: number; z: number } | null {
  if (!Number.isFinite(factor) || factor < 0) return null;
  let bestY = -Infinity;
  for (const v of volumes) {
    const dx = cursor.x - v.x;
    let near: number;
    let far: number;
    if (v.shape === 'circle') {
      if (Math.abs(dx) > v.r) continue;
      const s = Math.sqrt(v.r * v.r - dx * dx);
      near = v.z - s;
      far = v.z + s;
    } else {
      if (Math.abs(dx) > v.hx) continue;
      near = v.z - v.hz;
      far = v.z + v.hz;
    }
    // The heights at which the line of sight is over the footprint, within what is drawn of the volume.
    let top: number;
    if (factor === 0) {
      if (cursor.z < near || cursor.z > far) continue;
      top = v.y + v.height;
    } else {
      const lo = Math.max(v.y, (near - cursor.z) / factor);
      top = Math.min(v.y + v.height, (far - cursor.z) / factor);
      if (top < lo) continue;
    }
    if (top > bestY) bestY = top;
  }
  if (bestY === -Infinity) return null;
  return { x: cursor.x, z: cursor.z + bestY * factor };
}
